import { useMemo } from 'react';
import { Tooltip } from '@material-ui/core';
import cx from 'classnames';
import { capitalize, omit } from 'lodash';
import PropTypes from 'prop-types';

import { FeedbackIcon } from 'src/assets/icons/FeedbackIcon';
import {
  CUSTOM_BUTTON_SIZE,
  CUSTOM_BUTTON_VARIANT,
  CustomButton,
} from 'src/components/CustomButton';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { Loader } from 'src/components/Loader';
import TextLink from 'src/components/TextLink';
import {
  getToolDefaultAssetType,
  getToolMixtureSubAssetTypeSubtitles,
  getToolMixtureSubGenerationTools,
  getToolTitleForTooltip,
} from 'src/data/generationTool';

import { RatingsScore } from '../RatingsScore';

import { useStyles } from './useStyles';

const RATINGS_THRESHOLD = 5;

export const PersonalizeButton = ({
  ratingsCount,
  generationTool,
  onClearVariationRatings,
  onPersonalizePromptClick,
  disabled,
  loading,
}) => {
  const styles = useStyles();

  const score = (ratingsCount) => {
    if (ratingsCount === 0) {
      return 0;
    } else if (ratingsCount <= RATINGS_THRESHOLD) {
      // need to make sure we see some progress
      return Math.max((25 / RATINGS_THRESHOLD) * ratingsCount, 12);
    } else {
      return Math.min(25 + ratingsCount - RATINGS_THRESHOLD, 100);
    }
  };

  const ratingsCountDisabled = ratingsCount?.disabled;

  const filteredRatingsCount = useMemo(() => omit(ratingsCount, 'disabled'), [ratingsCount]);

  const moreRatingsNeeded = useMemo(
    () =>
      Object.keys(filteredRatingsCount).every((key) => {
        return (
          filteredRatingsCount[key]?.positive < RATINGS_THRESHOLD &&
          filteredRatingsCount[key]?.negative < RATINGS_THRESHOLD
        );
      }),
    [filteredRatingsCount]
  );

  const sortedRatingsCountKeys = useMemo(() => {
    const mixtureTools = getToolMixtureSubGenerationTools(generationTool);
    const assetTypes = mixtureTools.map((tool) => getToolDefaultAssetType(tool));
    const keys = Object.keys(filteredRatingsCount);

    return keys.length > 1
      ? keys.sort((a, b) => assetTypes.indexOf(a) - assetTypes.indexOf(b))
      : keys;
  }, [generationTool, filteredRatingsCount]);

  return (
    <Tooltip
      interactive
      classes={{ tooltip: styles.tooltipRatings, popper: styles.popper }}
      placement={'bottom'}
      title={
        <div
          className={styles.ratingsBannerWrapper}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <CustomTypography
            className={styles.ratingsHeader}
            variant={TYPOGRAPHY_VARIANT.medium16}
          >
            Personalize your prompt
          </CustomTypography>
          <CustomTypography
            className={styles.ratingsSubheader}
            variant={TYPOGRAPHY_VARIANT.paragraph14}
          >
            Automatically add instructions to your prompt based on your past content ratings
          </CustomTypography>

          {sortedRatingsCountKeys.length > 0 &&
            (moreRatingsNeeded || (ratingsCountDisabled && !moreRatingsNeeded)) && (
              <div className={styles.moreRatings}>
                <CustomTypography variant={TYPOGRAPHY_VARIANT.medium12}>
                  {moreRatingsNeeded
                    ? 'More ratings are needed to personalize this prompt'
                    : 'Still learning your preferences, keep rating!'}
                </CustomTypography>
              </div>
            )}

          <div className={styles.ratingWrapper}>
            {sortedRatingsCountKeys.length === 0 && (
              <Loader
                className={styles.ratingsLoader}
                size={32}
              />
            )}
            {sortedRatingsCountKeys.map((key, index) => {
              return (
                <div
                  className={styles.ratingScoreWrapper}
                  key={`ratingCount${key}`}
                >
                  <CustomTypography
                    className={styles.ratingScoreName}
                    variant={TYPOGRAPHY_VARIANT.medium12}
                  >
                    <span>
                      {sortedRatingsCountKeys.length > 1
                        ? capitalize(getToolMixtureSubAssetTypeSubtitles(generationTool, key))
                        : getToolTitleForTooltip(generationTool)}
                    </span>{' '}
                    ratings
                  </CustomTypography>
                  <div className={styles.ratingsPair}>
                    {Object.keys(ratingsCount[key]).map((ratingKey) => {
                      return (
                        <div
                          key={`ratingCount${key}${ratingKey}`}
                          className={styles.ratingScore}
                        >
                          <RatingsScore
                            score={score(ratingsCount[key][ratingKey])}
                            number={ratingsCount[key][ratingKey]}
                            threshold={RATINGS_THRESHOLD}
                            negative={ratingKey === 'negative'}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <CustomTypography
            className={styles.clearRatings}
            variant={TYPOGRAPHY_VARIANT.regular12}
          >
            Want to start over?{' '}
            <TextLink onClick={onClearVariationRatings}>Clear rating history</TextLink>
          </CustomTypography>
        </div>
      }
    >
      <div>
        <CustomButton
          className={cx(styles.footerButton, styles.improveButton)}
          variant={CUSTOM_BUTTON_VARIANT.text}
          size={CUSTOM_BUTTON_SIZE.smaller}
          onClick={onPersonalizePromptClick}
          startIcon={
            loading ? <Loader size={16} /> : <FeedbackIcon className={styles.improveStartIcon} />
          }
          classes={{ startIcon: styles.startIcon }}
          disabled={disabled || moreRatingsNeeded || ratingsCountDisabled}
        >
          Personalize
        </CustomButton>
      </div>
    </Tooltip>
  );
};

PersonalizeButton.propTypes = {
  onPersonalizePromptClick: PropTypes.func,
  ratingsCount: PropTypes.shape(),
  generationTool: PropTypes.string,
  onClearVariationRatings: PropTypes.func,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
};
PersonalizeButton.defaultProps = {
  onPersonalizePromptClick: null,
  ratingsCount: null,
  generationTool: null,
  onClearVariationRatings: null,
  loading: false,
  disabled: false,
};
